/**
 * Weekly Insight Service
 *
 * Builds the aggregated weekly payload from Firestore data already
 * available on the frontend, then calls the backend weekly insight endpoints.
 *
 * FE responsibility: data assembly only.
 * BE responsibility: insight selection, headline, subtext, Core/Trak/GLP-1 logic.
 */

import { env } from '../utils/env';
import { loggedFetch } from '../utils/apiLogger';
import { getFirebaseIdToken } from '../utils/firebaseToken';
import { getNutritionEntriesForDateRange } from './nutrition.service';
import { getWorkoutEntriesForDateRange } from './workout.service';
import type { TargetRanges } from '../types/nutrition';
import dayjs from 'dayjs';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface WeeklyPayload {
  weekOf: string;
  loggedDays: number;
  avgProtein: number;
  avgProteinTargetMin: number;
  avgHydration: number;
  hydrationMin: number;
  avgFibre: number;
  fibreMin: number;
  avgDeficitPct: number;
  proteinDaysOnTarget: number;
  hydrationDaysOnTarget: number;
  fibreDaysOnTarget: number;
  strengthSessionsLast7: number;
  strengthSessionsLast30: number;
  isPremium: boolean;
  glp1Enabled: boolean;
}

export interface WeeklyInsightResponse {
  weekOf: string;
  version: number;
  hasData: boolean;
  insight: string;
  headline: string | null;
  subtext: string | null;
  subtextVisible: boolean;
  ctaVisible: boolean;
  ctaText: string | null;
}

// ── Date helpers ───────────────────────────────────────────────────────────────

/**
 * Returns the Monday ISO date string (YYYY-MM-DD) for the current week.
 * weekOf is always Monday of the current week.
 */
export function getMondayOf(date: Date): string {
  const d = dayjs(date);
  const day = d.day(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day;
  return d.add(diff, 'day').format('YYYY-MM-DD');
}

/**
 * Returns the current week to date: Monday of this week through today.
 * e.g. on Friday 20 March → 2026-03-16 through 2026-03-20
 */
function getCurrentWeekWindow(weekOf: string): { start: string; end: string } {
  const today = dayjs().format('YYYY-MM-DD');
  return { start: weekOf, end: today };
}

/**
 * Returns the localStorage key used to track whether we've already computed
 * the weekly insight today for this weekOf.
 * Key format: weeklyInsightComputed:{weekOf}:{yyyy-mm-dd}
 */
export function getDailyComputeKey(weekOf: string): string {
  const today = dayjs().format('YYYY-MM-DD');
  return `weeklyInsightComputed:${weekOf}:${today}`;
}

/**
 * Returns true if the weekly insight has already been computed today
 * for the current weekOf.
 */
export function hasComputedToday(weekOf: string): boolean {
  try {
    return localStorage.getItem(getDailyComputeKey(weekOf)) === 'true';
  } catch {
    return false;
  }
}

/**
 * Marks the weekly insight as computed for today's date and current weekOf.
 */
export function markComputedToday(weekOf: string): void {
  try {
    localStorage.setItem(getDailyComputeKey(weekOf), 'true');
  } catch {
    // ignore
  }
}

// ── Payload builder ────────────────────────────────────────────────────────────

/**
 * Builds the aggregated weekly payload from Firestore data.
 * Reads nutritionEntries and workoutEntries for the prior Monday–Sunday window.
 */
export async function buildWeeklyPayload(
  userId: string,
  weekOf: string,
  targets: TargetRanges,
  isPremium: boolean,
  glp1Enabled: boolean,
  userTdee: number,
): Promise<WeeklyPayload> {
  const { start, end } = getCurrentWeekWindow(weekOf);

  const thirtyDaysAgo = dayjs(start).subtract(30, 'day').format('YYYY-MM-DD');
  const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');

  const [nutritionEntries, workoutsCurrentWeek, workoutsLast30] = await Promise.all([
    getNutritionEntriesForDateRange(userId, start, end),
    getWorkoutEntriesForDateRange(userId, start, end),
    getWorkoutEntriesForDateRange(userId, thirtyDaysAgo, yesterday),
  ]);

  const proteinMin = targets.protein.min ?? 0;
  const hydrationMin = targets.hydration.min ?? 0;
  const fibreMin = targets.fibre.min ?? 0;

  // Group nutrition entries by date — a logged day has at least one entry
  const byDate = new Map<string, typeof nutritionEntries>();
  for (const entry of nutritionEntries) {
    if (!byDate.has(entry.date)) byDate.set(entry.date, []);
    byDate.get(entry.date)!.push(entry);
  }

  const loggedDates = Array.from(byDate.keys());
  const loggedDays = loggedDates.length;

  // Per-day aggregates
  let totalProtein = 0;
  let totalHydration = 0;
  let totalFibre = 0;
  let totalDeficitPct = 0;
  let proteinDaysOnTarget = 0;
  let hydrationDaysOnTarget = 0;
  let fibreDaysOnTarget = 0;

  for (const date of loggedDates) {
    const entries = byDate.get(date)!;
    const dayProtein = entries.reduce((s, e) => s + (e.macros?.protein ?? 0), 0);
    const dayCalories = entries.reduce((s, e) => s + (e.macros?.calories ?? 0), 0);
    const dayHydration = entries.reduce((s, e) => s + (e.hydrationMl ?? 0), 0);
    const dayFibre = entries.reduce((s, e) => s + (e.macros?.fibre ?? 0), 0);

    totalProtein += dayProtein;
    totalHydration += dayHydration;
    totalFibre += dayFibre;

    if (userTdee > 0) {
      const deficitPct = (userTdee - dayCalories) / userTdee;
      totalDeficitPct += deficitPct;
    }

    if (dayProtein >= proteinMin) proteinDaysOnTarget++;
    if (dayHydration >= hydrationMin) hydrationDaysOnTarget++;
    if (dayFibre >= fibreMin) fibreDaysOnTarget++;
  }

  const days = loggedDays || 1;
  const avgProtein = Math.round(totalProtein / days);
  const avgHydration = Math.round(totalHydration / days);
  const avgFibre = Math.round(totalFibre / days);
  const avgDeficitPct = userTdee > 0 ? totalDeficitPct / days : 0;

  const strengthSessionsLast7 = workoutsCurrentWeek.filter(w => w.type === 'weightlifting').length;
  const strengthSessionsLast30 = workoutsLast30.filter(w => w.type === 'weightlifting').length;

  return {
    weekOf,
    loggedDays,
    avgProtein,
    avgProteinTargetMin: proteinMin,
    avgHydration,
    hydrationMin,
    avgFibre,
    fibreMin,
    avgDeficitPct: Math.round(avgDeficitPct * 1000) / 1000,
    proteinDaysOnTarget,
    hydrationDaysOnTarget,
    fibreDaysOnTarget,
    strengthSessionsLast7,
    strengthSessionsLast30,
    isPremium,
    glp1Enabled,
  };
}

// ── API calls ──────────────────────────────────────────────────────────────────

/**
 * POST /api/weekly-insight/compute
 * Sends the aggregated weekly payload to the backend.
 * Backend computes and stores the insight, returns the result.
 */
export async function computeWeeklyInsight(
  payload: WeeklyPayload,
): Promise<WeeklyInsightResponse> {
  const token = await getFirebaseIdToken();
  const response = await loggedFetch(`${env.apiBaseUrl}/api/weekly-insight/compute`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.message || `HTTP ${response.status}`);
  }
  return response.json() as Promise<WeeklyInsightResponse>;
}

/**
 * GET /api/weekly-insight/latest
 * Returns the latest stored weekly insight for the authenticated user.
 */
export async function fetchLatestWeeklyInsight(): Promise<WeeklyInsightResponse> {
  const token = await getFirebaseIdToken();
  const response = await loggedFetch(`${env.apiBaseUrl}/api/weekly-insight/latest`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.message || `HTTP ${response.status}`);
  }
  return response.json() as Promise<WeeklyInsightResponse>;
}
